import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import 'aframe';

// Custom A-Frame 3D Logo Loader Component
const LogoLoader3D = () => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [modelError, setModelError] = useState(false);
  const sceneRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    const initializeAFrame = () => {
      if (typeof window !== 'undefined' && window.AFRAME) {
        console.log('A-Frame loaded successfully');
        
        // Simulate loading progress
        const progressInterval = setInterval(() => {
          setLoadingProgress(prev => {
            if (prev >= 100) {
              clearInterval(progressInterval);
              if (mounted) {
                setTimeout(() => setIsLoaded(true), 500);
              }
              return 100;
            }
            return prev + Math.random() * 15;
          });
        }, 200);

        // Setup model loading handlers
        setTimeout(() => {
          const scene = sceneRef.current;
          if (scene) {
            // Listen for model loading events
            scene.addEventListener('model-loaded', () => {
              console.log('3D Logo model loaded successfully');
              setModelError(false);
            });

            scene.addEventListener('model-error', () => {
              console.warn('3D Logo model failed to load, using fallback');
              setModelError(true);
            });
          }
        }, 1000);

        return () => {
          if (progressInterval) clearInterval(progressInterval);
        };
      } else {
        setTimeout(initializeAFrame, 100);
      }
    };

    initializeAFrame();

    // Cleanup
    return () => {
      mounted = false;
    };
  }, []);

  if (isLoaded) {
    return (
      <div style={{ width: '100vw', height: '100vh', background: '#000' }}>
        <a-scene
          ref={sceneRef}
          loading-screen="enabled: false"
          vr-mode-ui="enabled: false"
          device-orientation-permission-ui="enabled: false"
          renderer="antialias: true; colorManagement: true"
          background="color: #000000"
          style={{ width: '100%', height: '100%' }}
        >
          <a-assets>
            <a-asset-item id="logo3d" src="/assets/images/3d-logo.glb"></a-asset-item>
            <img id="logoTexture" src="/assets/images/logo.png" alt="Company Logo" />
          </a-assets>

          {/* Lighting Setup */}
          <a-light type="ambient" color="#ffffff" intensity="0.5"></a-light>
          <a-light 
            type="directional" 
            color="#ffffff" 
            intensity="1.0" 
            position="4 6 4"
            light="castShadow: true"
          ></a-light>
          <a-light 
            type="point" 
            color="#D4AF37" 
            intensity="0.8" 
            position="-3 3 2"
          ></a-light>

          {/* 3D Logo Model */}
          {!modelError && (
            <a-entity
              id="main-logo"
              gltf-model="#logo3d"
              position="0 0 -6"
              scale="3 3 3"
              rotation="0 0 0"
              animation="property: rotation; to: 0 360 0; loop: true; dur: 15000; easing: linear"
              animation__float="property: position; to: 0 0.5 -6; dir: alternate; loop: true; dur: 5000; easing: easeInOutSine"
              animation__scale="property: scale; to: 3.2 3.2 3.2; dir: alternate; loop: true; dur: 8000; easing: easeInOutQuad"
            ></a-entity>
          )}

          {/* Fallback Logo if GLB fails */}
          {modelError && (
            <a-box
              id="fallback-logo"
              material="src: #logoTexture; transparent: true; alphaTest: 0.1; emissive: #D4AF37; emissiveIntensity: 0.2"
              position="0 0 -6"
              scale="3 3 1"
              rotation="0 0 0"
              animation="property: rotation; to: 0 360 0; loop: true; dur: 10000; easing: linear"
              animation__float="property: position; to: 0 0.5 -6; dir: alternate; loop: true; dur: 5000; easing: easeInOutSine"
              animation__glow="property: material.emissiveIntensity; to: 0.5; dir: alternate; loop: true; dur: 3000; easing: easeInOutSine"
            ></a-box>
          )}

          {/* Brand Particles */}
          <a-sphere
            color="#D4AF37"
            opacity="0.8"
            radius="0.1"
            position="4 2 -4"
            material="emissive: #D4AF37; emissiveIntensity: 0.6; transparent: true"
            animation="property: position; to: -4 3 -8; dir: alternate; loop: true; dur: 6000; easing: easeInOutQuad"
            animation__pulse="property: scale; to: 2 2 2; dir: alternate; loop: true; dur: 4000; easing: easeInOutSine"
          ></a-sphere>

          <a-sphere
            color="#006A6A"
            opacity="0.9"
            radius="0.08"
            position="-3 1 -3"
            material="emissive: #006A6A; emissiveIntensity: 0.8; transparent: true"
            animation="property: position; to: 3 4 -5; dir: alternate; loop: true; dur: 7000; easing: easeInOutSine"
          ></a-sphere>

          <a-sphere
            color="#008000"
            opacity="0.7"
            radius="0.06"
            position="2 -1 -4"
            material="emissive: #008000; emissiveIntensity: 0.5; transparent: true"
            animation="property: position; to: -2 3 -6; dir: alternate; loop: true; dur: 8000; easing: easeInOutCubic"
          ></a-sphere>

          {/* Camera */}
          <a-camera
            position="0 2 0"
            look-controls="enabled: true"
            wasd-controls="enabled: true"
            fov="50"
          ></a-camera>
        </a-scene>
      </div>
    );
  }

  // Loading Screen
  return (
    <div className="min-h-screen gradient-background-main flex items-center justify-center relative overflow-hidden">
      <div className="text-center z-10">
        {/* 3D CSS Logo Preview */}
        <div className="w-80 h-80 mx-auto mb-8 flex items-center justify-center perspective-1000">
          <motion.div
            className="relative w-40 h-40 transform-style-preserve-3d"
            animate={{
              rotateY: [0, 360],
              rotateX: [0, 20, 0, -20, 0],
            }}
            transition={{
              rotateY: {
                duration: 6,
                repeat: Infinity,
                ease: "linear"
              },
              rotateX: {
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }
            }}
          >
            <div className="absolute inset-0 w-full h-full transform translate-z-8">
              <div className="w-full h-full bg-white/10 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/30 flex items-center justify-center p-6">
                <img
                  src="/assets/images/logo.png"
                  alt="Exquisite Coral Africa Limited Logo"
                  className="w-full h-full object-contain filter drop-shadow-lg brightness-110"
                />
              </div>
            </div>
            
            <div className="absolute inset-0 w-full h-full transform rotate-y-180 translate-z-8">
              <div className="w-full h-full bg-secondary/30 backdrop-blur-sm rounded-3xl shadow-2xl border border-secondary/40 flex items-center justify-center">
                <div className="text-white text-3xl font-bold font-montserrat">ECA</div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Loading Progress */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mb-8"
        >
          {/* Progress Bar */}
          <div className="max-w-md mx-auto">
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-secondary via-white to-accent rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${loadingProgress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LogoLoader3D; 